import {
  CalendarOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  ExportOutlined,
  FileExcelOutlined,
  LinkOutlined,
  LoadingOutlined,
  NodeIndexOutlined,
  PlusOutlined,
  ProfileOutlined,
  StopOutlined,
  UndoOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Badge,
  Button,
  Col,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Popover,
  Row,
  Select,
  Spin,
  Statistic,
  Switch,
  Table,
  Tag,
  Timeline,
  Tooltip,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import { get } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useToggle } from 'react-use';
import styled from 'styled-components';

import TableFilterAdvanced from '../../components/TableFilterAdvanced';
import useTable from '../../components/Template/TableTemplate';
import XeroSyncButton from '../../components/XeroSyncButton';
import XeroSyncHistory from '../../components/XeroSyncHistory';
import { URL_ORDER_SYNC_TO_XERO } from '../../constants/URLS';
import { useOrderStatus } from '../../custom-hooks/sa-orders';
import { useSAUsers } from '../../custom-hooks/sa-users';
import { downloadFile, fetchAPI } from '../../utils/api';
import {
  formatPrice,
  formatUTCTimeToLocal,
  getCurrencyFromRegion,
} from '../../utils/format';
import useWindowDimension from '../../utils/useWindowDimension';
import SupplierPaymentPopOver from '../SupplierPayments/SupplierPaymentPopOver';
import { safelyParseJSON } from '../Ticketing/SubmittedTicketList';
import DownloadBulkInvoice from './DownloadBulkInvoice';
import DownloadInvoice from './DownloadInvoice';
import EditInvoiceDateInTable from './EditInvoiceDateInTable';
import EditDoDateInTable from './EditDoDateInTable';
import FilterMultipleEntity from './FilterMultipleEntity';
import InvoiceConsolidation from './InvoiceConsolidation';
import OrderCreate from './OrderCreate';
import OrderGRNReminder from './OrderGRNReminder';
import OrderInputTrackingNumber from './OrderInputTrackingNumber';
import OrderInvoiceDateHistory from './OrderInvoiceDateHistory';
import OrderDoDateHistory from './OrderDoDateHistory';
import OrderItemDeliveryDate from './OrderListV2/OrderItemDeliveryDate';
import OrderOperationStatusActions from './OrderOperationStatusActions';
import OrderPaymentDate from './OrderPaymentDate';
import OrderPaymentStatus from './OrderPaymentStatus';
import OrderPaymentStatusTracking from './OrderPaymentStatusTracking';
import OrderRejectionForm, { reasonOptions } from './OrderRejectionForm';
import OrderSendInvoice from './OrderSendInvoice';
import OrderSendReportMenu from './OrderSendReportMenu';
import OrderShipmentStatus from './OrderShipmentStatus';
import OrderShipmentStatusTracking from './OrderShipmentStatusTracking';
import OrderTotalPOValues, {
  OrderFXRates,
  OrderTotalClientPaidValues,
  OrderTotalPOFulfilledValues,
  TotalPOProvider,
} from './OrderTotalPOValues';
import ReorderColumnsOrderV1, {
  defaultOrderColumnsV1,
  OrderColumn,
} from './ReorderColumnsOrderV1';
import SupplierPaymentCreateFromOrder from './SupplierPaymentCreateFromOrder';
import { ColorMap, formatOrder, OrderOperationStatus } from './utils';
import OrderEmailOrderPicker from './OrderEmailOrderPicker';
import OrderGenerateNcprSpid from './OrderGenerateNcprSpid';
import OrderModalMapExternalOrder from './OrderModalMapExternalOrder';
import OrderCorrectedEmailReader from './OrderCorrectedEmailReader';
import ShippingAddressFromER from './OrderListV2/ShippingAddressFromER';

const paymentStatus = [
  'PENDING INVOICE SUBMISSION',
  'INVOICE_DISPUTED',
  'PARTIALLY_PAID',
  'PAID',
  'REFUNDED',
  'PENDING PAYMENT',
];

const orderV1ColumnsKey = 'order-v1-columns-order';

export const GenerateDO = ({
  record,
  onOpenChange,
}: {
  record: any;
  onOpenChange?: (open: boolean) => void;
}) => {
  const [loading, toggleLoading] = useToggle(false);

  const title = 'Generate DO';

  return (
    <Tooltip title={title}>
      <Button
        loading={loading}
        icon={<ExportOutlined />}
        onClick={async () => {
          // Close dropdown immediately when clicked
          onOpenChange?.(false);
          toggleLoading();
          try {
            const blob = await fetchAPI('order/gen_do', {
              payload: {
                id: record.id,
              },
              method: 'POST',
            });
            downloadFile(blob, `DO_${record.id}_${record.po_reference_number}`);
          } catch (e) {}

          toggleLoading();
        }}
      >
        Generate DO
      </Button>
    </Tooltip>
  );
};

export const TrackingStatus = ({
  record,
  onOpenChange,
}: {
  record: any;
  onOpenChange?: (open: boolean) => void;
}) => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const orderStatus = useOrderStatus(record.id, visible);

  const manualRefreshData = async () => {
    setLoading(true);
    await orderStatus.refetch();
    setLoading(false);
  };
  const generalLoading =
    orderStatus.isLoading || orderStatus?.data?.loading || loading;

  return (
    <>
      <Tooltip title={'Tracking Status'}>
        <Button
          icon={<NodeIndexOutlined />}
          onClick={() => {
            // Close dropdown immediately when clicked
            onOpenChange?.(false);
            setVisible(true);
          }}
        >
          Tracking Status
        </Button>
      </Tooltip>
      <Modal
        title={
          <span>
            Operation Status Tracking
            {generalLoading ? (
              <LoadingOutlined style={{ marginLeft: 8 }} />
            ) : (
              <UndoOutlined
                onClick={() => manualRefreshData()}
                style={{ marginLeft: 8, cursor: 'pointer' }}
              />
            )}
          </span>
        }
        visible={visible}
        onCancel={() => setVisible(false)}
        footer={null}
        width={600}
      >
        <Spin spinning={generalLoading}>
          <StatusTimeline>
            <Timeline>
              {orderStatus?.data?.operation?.map(status => (
                <Timeline.Item
                  key={status?.timestamp}
                  dot={<CalendarOutlined />}
                >
                  <strong>{status?.next_status}</strong>
                  <span
                    style={{
                      display: 'block',
                      fontSize: '12px',
                      opacity: 0.8,
                      marginTop: 4,
                    }}
                  >
                    {formatUTCTimeToLocal(
                      status?.timestamp,
                      'DD MMM YYYY, HH:mm (Z)'
                    )}
                  </span>
                  <Tooltip
                    title={`${status?.user_info?.email}`}
                    placement="topLeft"
                  >
                    <div style={{ fontSize: '12px', marginTop: 2 }}>
                      <UserOutlined /> By {status?.user_info?.full_name}
                    </div>
                  </Tooltip>
                </Timeline.Item>
              ))}
              {(!orderStatus?.data?.operation ||
                orderStatus?.data?.operation.length === 0) && (
                <Timeline.Item>
                  <span style={{ opacity: 0.6 }}>
                    No tracking history available
                  </span>
                </Timeline.Item>
              )}
            </Timeline>
          </StatusTimeline>
        </Spin>
      </Modal>
    </>
  );
};

export const CancelPOButton = ({
  record,
  refreshList,
  onOpenChange,
}: {
  record: any;
  refreshList: () => void;
  onOpenChange?: (open: boolean) => void;
}) => {
  const [reasonModalVisible, setReasonModalVisible] = useState(false);
  const [checking, setChecking] = useState(false);
  const [blockedModalVisible, setBlockedModalVisible] = useState(false);
  const [canCancelSpids, setCanCancelSpids] = useState<
    Array<{
      id: number;
      status: string;
      approval_status: string;
      remittance_status: string;
      refund_status: string;
    }>
  >([]);
  const [blockedSpids, setBlockedSpids] = useState<
    Array<{
      id: number;
      status: string;
      approval_status: string;
      remittance_status: string;
      refund_status: string;
    }>
  >([]);
  const user = useSelector((state: AppState) => state.user);
  if (user.team === 'FINANCE') {
    return null;
  }

  const checkIfCanCancel = async () => {
    setChecking(true);
    try {
      const resp = (await fetchAPI(`orders/${record.id}/can-cancel`)) as {
        allowed: boolean;
        spids: Array<{
          id: number;
          status: string;
          approval_status: string;
          remittance_status: string;
          refund_status: string;
        }>;
      };

      setCanCancelSpids(resp.spids || []);

      if (resp.allowed) {
        setReasonModalVisible(true);
      } else {
        setBlockedSpids(resp.spids || []);
        setBlockedModalVisible(true);
      }
    } catch (error) {
      message.error('Failed to check if order can be cancelled');
    }
    setChecking(false);
  };

  const spidColumns = [
    { title: 'SPID', dataIndex: 'id', key: 'id' },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    {
      title: 'Approval Status',
      dataIndex: 'approval_status',
      key: 'approval_status',
    },
    {
      title: 'Remittance Status',
      dataIndex: 'remittance_status',
      key: 'remittance_status',
    },
    {
      title: 'Refund Status',
      dataIndex: 'refund_status',
      key: 'refund_status',
    },
  ];

  return (
    <>
      <Modal
        title="Cannot Cancel Order"
        visible={blockedModalVisible}
        onCancel={() => setBlockedModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setBlockedModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="proceed"
            type="primary"
            danger
            onClick={() => {
              setBlockedModalVisible(false);
              setReasonModalVisible(true);
            }}
          >
            Proceed Anyway
          </Button>,
        ]}
        width={700}
      >
        <p>
          The following Order already have Supplier Payments linked, cancelling
          the Order might also cancel the linked Supplier Payments. Please
          review the linked Supplier Payments below before proceeding.
        </p>
        <Table
          columns={spidColumns}
          dataSource={blockedSpids}
          rowKey="id"
          pagination={false}
          size="small"
          style={{ marginTop: 12 }}
        />
      </Modal>
      <Modal
        title="Please Provide Information for PO cancellation"
        visible={reasonModalVisible}
        onCancel={() => setReasonModalVisible(false)}
        footer={null}
        width={500}
      >
        <OrderRejectionForm
          defaultValue={record}
          spidData={canCancelSpids}
          onSubmit={async values => {
            try {
              await fetchAPI(`orders/${record.id}/set-cancel-reason`, {
                method: 'POST',
                payload: {
                  reason: values?.reason,
                  spid: values?.spid_number ?? undefined,
                },
              });
              setReasonModalVisible(false);
              refreshList();
            } catch (error) {
              message.error('Failed change order operation status');
            }
          }}
        />
      </Modal>
      <Tooltip title="Cancel Order">
        <Button
          icon={<StopOutlined />}
          loading={checking}
          onClick={() => checkIfCanCancel()}
        >
          {checking ? 'Checking...' : 'Cancel PO'}
        </Button>
      </Tooltip>
    </>
  );
};

export const getOrderDetail = async (id: number) => {
  try {
    return fetchAPI(`orders/${id}/lite`);
  } catch (error) {
    message.error('Failed get order detail');
    return null;
  }
};

export default () => {
  const [visible, toggle] = useToggle(false);
  const user = useSelector((state: AppState) => state.user);
  const [sortedColumns, setSortedColumns] = useState<OrderColumn[]>(
    defaultOrderColumnsV1
  );

  const { isSmallScreen } = useWindowDimension();
  // const [weeklyReport, setWeeklyReport] = useState(false);
  const [invoiceConsolidationDrawer, setInvoiceConsolidationDrawer] = useState(
    false
  );
  const [orderId, setOrderId] = useState(0);
  const [
    openModalDownloadBulkInvoice,
    setOpenModalDownloadBulkInvoice,
  ] = useState(false);

  const users = useSAUsers(user);

  const expandedRowRender = record => {
    const productColumns = [
      {
        title: 'Name',
        key: 'name',
        width: 200,
        render: record => (
          <div style={{ maxWidth: 300 }}>
            <b>{record.name}</b>
            {record?.note && (
              <span
                style={{ display: 'block', marginTop: '5px', fontSize: '12px' }}
              >
                <b>Note : </b>
                {record?.note}
              </span>
            )}
            {record?.category_item?.name && (
              <span
                style={{ display: 'block', marginTop: '5px', fontSize: '12px' }}
              >
                Category : <b>{record?.category_item?.name}</b>
              </span>
            )}
            {record?.supplier?.name && (
              <span
                style={{ display: 'block', marginTop: '5px', fontSize: '12px' }}
              >
                Supplier :{' '}
                <b>
                  [{record?.supplier?.id}] {record?.supplier?.name}
                </b>
              </span>
            )}
            {record?.supplier?.marketplace?.name && (
              <span
                style={{ display: 'block', marginTop: '5px', fontSize: '12px' }}
              >
                Billing Party :{' '}
                <b>
                  [{record?.supplier?.marketplace?.id}]{' '}
                  {record?.supplier?.marketplace?.name}
                </b>
              </span>
            )}
            <div
              style={{
                marginTop: '5px',
                fontSize: '12px',
              }}
            >
              <Tag>{record?.paid_by}</Tag>
            </div>
            {record?.external_url && (
              <span
                style={{
                  display: '-webkit-box',
                  marginTop: '5px',
                  fontSize: '12px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                External URL : <b>{record?.external_url}</b>
              </span>
            )}
            {record?.external_id && (
              <span
                style={{
                  display: 'block',
                  marginTop: '5px',
                  fontSize: '12px',
                }}
              >
                External ID : <b>{record?.external_id}</b>
              </span>
            )}
            <OrderItemDeliveryDate
              record={record}
              onSuccess={() => toggleLoad()}
            />
          </div>
        ),
      },
      {
        title: 'NCPR',
        key: 'is_ncpr',
        width: 120,
        render: record => {
          return <>{record.is_ncpr ? <span>Yes</span> : <span>No</span>}</>;
        },
      },
      { title: 'Quantity', dataIndex: 'quantity', width: 50 },
      {
        title: 'Related SPID(s)',
        render: record => {
          return (
            <UlWrapper
              style={{
                listStyleType: 'None',
                gap: '6px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {record.spid_no.map(spid => {
                return (
                  <SupplierPaymentPopOver
                    supplierPaymentId={spid}
                    status={spidStatusMap[spid]}
                    lineItemProblemStatus={
                      spidProblemStatusMap[spid]?.[record.id]
                    }
                  />
                );
              })}
            </UlWrapper>
          );
        },
        width: 50,
      },
      {
        title: 'Fulfilled Quantity',
        dataIndex: 'fulfilled_quantity',
        width: 50,
      },
      { title: 'Currency', dataIndex: 'currency', width: 20 },
      { title: 'Price', dataIndex: 'price', width: 50 },
      { title: 'Total', dataIndex: 'total', width: 50 },

      {
        title: 'Cost',
        dataIndex: 'cost',
        width: 50,
      },
      {
        title: 'Tax %',
        dataIndex: 'tax_rate',
        width: 50,
      },
      { title: 'Total Cost', dataIndex: 'total_cost', width: 50 },

      {
        title: 'Link',
        render: record => (
          <Button
            shape="circle"
            icon={<LinkOutlined />}
            target="_blank"
            href={record.external_url}
          />
        ),
        width: 50,
      },
    ];

    return (
      <div>
        <ExpanderBtnContainer>
          <Tooltip title="Copy Order Line Item">
            <Button
              icon={<CopyOutlined />}
              onClick={() => {
                const text = record.items.reduce((str, item, idx) => {
                  const cols = [
                    item.name,
                    item.category_item?.name || '-',
                    item.note || '-',
                    item.quantity,
                    item.fulfilled_quantity,
                    item.currency,
                    item.price || 0,
                    item.total || 0,
                    item.cost || 0,
                    item.tax_rate || 0,
                    item.total_cost || 0,
                  ];

                  return str + (!!idx ? '\n' : '') + cols.join('\t');
                }, '');

                navigator.clipboard
                  .writeText(text)
                  .then(() => {
                    message.success('Order Line Item copied');
                  })
                  .catch(() => {
                    message.error('Failed to copy Order Line Item');
                  });
              }}
            />
          </Tooltip>
        </ExpanderBtnContainer>
        <Table
          columns={productColumns}
          dataSource={record.items}
          pagination={false}
        />
      </div>
    );
  };

  const refreshList = () => {
    toggleLoad();
  };

  const handleClickEditOrder = async (
    recordId: number,
    doubleToggle = false
  ) => {
    const resp = await getOrderDetail(recordId);
    form.setFieldsValue(formatOrder(resp));
    setOrderId(recordId);
    toggle();
    if (doubleToggle) {
      toggle();
    }
  };
  const [filterNcpr, setFilterNcpr] = useState();
  const [filterWithoutPO, setFilterWithoutPO] = useState();
  const useTableState = useTable({
    useInternalPurchase: true,
    expandToggle: true,
    paginationPosition: 'bottom',
    showDateRange: true,
    showDownload: true,
    url: 'order/list',
    urlDownload: 'order/list/download',
    extraPicks: ['paid_by'],
    tableProps: {
      expandable: {
        expandedRowRender,
        rowExpandable: record => !!record.items.length,
        fixed: true,
      },
      scroll: { x: 1200, y: 'calc(100vh - 345px)' },
    },
    extra: users.data?.list?.length > 0 && (
      <>
        <Button
          icon={<PlusOutlined />}
          onClick={() => {
            const defaultFormValues = formatOrder(null);
            defaultFormValues.items = [];
            form.setFieldsValue(defaultFormValues);
            toggle();
            setOrderId(0);
          }}
        >
          Create Order
        </Button>
        <Button onClick={() => setOpenModalDownloadBulkInvoice(true)}>
          Download Bulk Invoice
        </Button>
        <OrderSendReportMenu />
        <Button
          icon={<ProfileOutlined />}
          onClick={() => setInvoiceConsolidationDrawer(true)}
        >
          Invoice Consolidation
        </Button>
        <OrderSendInvoice />
        <OrderModalMapExternalOrder />
        <ReorderColumnsOrderV1
          columns={sortedColumns}
          setColumns={payload => {
            setSortedColumns(payload);
            localStorage.setItem(orderV1ColumnsKey, JSON.stringify(payload));
          }}
        />
      </>
    ),
    overrideParams: {
      is_without_po: false,
      is_negative_margin: false,
      is_ncpr: null,
    },
    advanceSearch: [
      {
        payload_key: 'q_assignee',
        defaultValue: '',
        Component: props => {
          return (
            <Input.Search
              placeholder="Search Assignee"
              defaultValue={props.value}
              allowClear
              enterButton
              onSearch={value =>
                props.setAdvanceSearch(prev => ({
                  ...prev,
                  q_assignee: value || '',
                }))
              }
              style={{ maxWidth: 350, marginRight: 15 }}
            />
          );
        },
      },
      {
        payload_key: 'filters_entity_ids',
        defaultValue: [],
        Component: props => {
          return (
            <FilterMultipleEntity
              selected={props.value}
              onChange={values => {
                props.setAdvanceSearch(prev => ({
                  ...prev,
                  filters_entity_ids: values,
                }));
              }}
            />
          );
        },
        payloadFormatter: (payload, value) => {
          return {
            ...payload,
            filters: {
              ...(payload.filters || {}),
              entity_name: value,
            },
            filters_entity_ids: value,
          };
        },
        searchQueryParser: value => {
          if (!value) {
            return [];
          }

          if (Array.isArray(value)) {
            return value.map(i => Number(i));
          }

          return value;
        },
      },
      {
        payload_key: 'is_without_po',
        defaultValue: null,
        Component: props => {
          return (
            <Select
              style={{ width: 150 }}
              allowClear
              value={filterWithoutPO}
              defaultValue={props.value || null}
              onChange={value => {
                props.setAdvanceSearch(prev => ({
                  ...prev,
                  is_without_po: value || null,
                }));
                setFilterWithoutPO(value);
              }}
              placeholder="Without PO?"
              options={[
                { value: 'True', label: 'Without PO' },
                { value: 'False', label: 'With PO' },
              ]}
            />
          );
        },
      },
      {
        payload_key: 'is_negative_margin',
        defaultValue: false,
        Component: props => {
          return (
            <Switch
              style={{ marginRight: '15px' }}
              defaultChecked={false}
              checked={props.value}
              onChange={value =>
                props.setAdvanceSearch(prev => ({
                  ...prev,
                  is_negative_margin: value || false,
                }))
              }
              checkedChildren="Negative Margin"
              unCheckedChildren="Positive Margin"
            />
          );
        },
      },
      {
        payload_key: 'is_ncpr',
        defaultValue: '',
        Component: props => {
          return (
            <Select
              style={{ width: 120 }}
              allowClear
              value={filterNcpr}
              defaultValue={props.value || undefined}
              onChange={value => {
                props.setAdvanceSearch(prev => ({
                  ...prev,
                  is_ncpr: value,
                }));
                setFilterNcpr(value);
              }}
              placeholder="NCPR"
              options={[
                { value: 'True', label: 'Yes' },
                { value: 'False', label: 'No' },
              ]}
            />
          );
        },
      },
      {
        payload_key: 'filter_xero_sync_status',
        defaultValue: '',
        Component: props => {
          return (
            <Select
              placeholder="Filter Xero Sync Status"
              // style={{ marginRight: 10, width: 200 }}
              style={{ width: 200 }}
              allowClear
              defaultValue={props.value || undefined}
              onChange={value =>
                props.setAdvanceSearch(prev => ({
                  ...prev,
                  filter_xero_sync_status: value || '',
                }))
              }
            >
              {['SYNCED', 'NOT_SYNCED'].map(stat => (
                <Select.Option key={stat} value={stat}>
                  {stat.split('_').join(' ')}
                </Select.Option>
              ))}
            </Select>
          );
        },
      },
      // {
      //   payload_key: 'filter_margin',
      //   defaultValue: null,
      //   Component: props => {
      //     return (
      //       <InputNumber
      //         value={props.value}
      //         placeholder="Filter Margin Start From"
      //         // onBlur={e => {
      //         //   console.log(e);
      //         // }}
      //       />
      //     );
      //   },
      // },
      {
        payload_key: 'filter_paid_by',
        defaultValue: '',
        Component: props => {
          return (
            <Select
              placeholder="Filter SS Paid or Client Paid"
              // style={{ marginRight: 10, width: 200 }}
              style={{ width: 200 }}
              allowClear
              defaultValue={props.value || undefined}
              onChange={value =>
                props.setAdvanceSearch(prev => ({
                  ...prev,
                  filter_paid_by: value || '',
                }))
              }
            >
              {['SS_PAID', 'CLIENT_PAID'].map(stat => (
                <Select.Option key={stat} value={stat}>
                  {stat.split('_').join(' ')}
                </Select.Option>
              ))}
            </Select>
          );
        },
      },
    ],
    columns: [
      {
        title: 'Actions',
        width: 150,
        fixed: 'left',
        render: record => (
          <StyledDiv>
            <p className="action-label">Operation</p>
            <OrderOperationStatusActions
              record={record}
              user={user}
              refreshList={() => refreshList()}
            />
            <Button
              shape="circle"
              icon={<EditOutlined />}
              onClick={() => handleClickEditOrder(record.id)}
            />
            <CancelPOButton record={record} refreshList={() => refreshList()} />
            {user.role === 'superadmin' && (
              <Popconfirm
                title="Are you sure to delete this Order Item?"
                onConfirm={async () => {
                  const hide = message.loading('Deleting...');
                  await fetchAPI(`order/delete`, {
                    payload: { id: record.id },
                    method: 'POST',
                  });
                  hide();
                  refreshList();
                }}
                onCancel={() => {}}
                okText="Yes"
                cancelText="No"
              >
                <Button shape="circle" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            )}
            <GenerateDO record={record} />
            <TrackingStatus record={record} />
            <OrderShipmentStatusTracking record={record} />
            <OrderInputTrackingNumber
              record={record}
              onSuccess={() => refreshList()}
            />
            <SupplierPaymentCreateFromOrder
              record={record}
              reloadOrderList={refreshList}
            />
            <OrderGRNReminder record={record} onSuccess={() => refreshList()} />
            <OrderGenerateNcprSpid
              orderId={record?.id}
              reloadOrderList={() => refreshList()}
            />
            {/* <RfqToSupplier recordId={record.id} /> */}
            <p className="action-label finance">Finance</p>
            <DownloadInvoice record={record} />
            <OrderPaymentStatusTracking record={record} />
            {record?.attachments?.length === 0 && (
              <span
                style={{
                  display: 'inline',
                  margin: '7px',
                  width: '32px',
                  padding: '6px',
                }}
              >
                <Tooltip title="No PO Attachments">
                  <Badge dot>
                    <FileExcelOutlined />
                  </Badge>
                </Tooltip>
              </span>
            )}
          </StyledDiv>
        ),
      },
      {
        title: 'SA ID',
        key: 'id',
        sorter: true,
        width: 85,
        fixed: 'left',
        render: record => {
          const orderStatus = record?.status_tracking?.order_status ?? [];
          const user = orderStatus[orderStatus?.length - 1]?.user_info ?? null;
          return (
            <>
              <span className="m-label">SA ID</span>
              {record?.reason ? (
                <>
                  <Popover
                    content={
                      <div style={{ maxWidth: '200px' }}>
                        {record?.reason}
                        <div
                          style={{
                            fontWeight: 'bold',
                            fontSize: '12px',
                            fontStyle: 'italic',
                            opacity: '0.7',
                          }}
                        >
                          By {user?.full_name} ({user?.email})
                        </div>
                      </div>
                    }
                    title="Reject Reason"
                  >
                    <span
                      style={{
                        color:
                          record?.operation_status === 'REJECTED'
                            ? 'red'
                            : 'inherit',
                      }}
                    >
                      {' '}
                      {record?.id}
                    </span>
                  </Popover>
                </>
              ) : (
                <>{record?.id}</>
              )}
            </>
          );
        },
      },
      ...implementSortedOrderColumns(
        [
          {
            title: 'Region Identifier',
            key: 'region_ref_number',
            sorter: true,
            width: 110,
            render: record => {
              return (
                <>
                  <span className="m-label">Region Identifier</span>
                  {record?.region_ref_number ?? '-'}
                </>
              );
            },
          },
          {
            title: 'Project Codes',
            key: 'project_code',
            width: 120,
            render: record => {
              const code = record?.project_code || '--';
              return (
                <>
                  <span className="m-label">Project Codes</span>
                  {code}
                </>
              );
            },
          },
          {
            title: 'PO Number',
            key: 'po_reference_number',
            width: 150,
            render: record => {
              return (
                <>
                  <span className="m-label">PO Number</span>
                  {record?.po_reference_number}
                  {record?.external_po_number ? (
                    <p style={{ margin: 0, fontSize: 12 }}>
                      External PO Number: <b>{record?.external_po_number}</b>
                    </p>
                  ) : null}
                  {record?.is_without_po ? (
                    <p style={{ margin: 0, fontSize: 12 }}>
                      Is Without PO: <b>Yes</b>
                    </p>
                  ) : null}
                  {record?.consolidate_po_number ? (
                    <p style={{ margin: 0, fontSize: 12 }}>
                      Consolidate Invoice Number:{' '}
                      <b>{record?.consolidate_po_number}</b>
                    </p>
                  ) : null}
                </>
              );
            },
          },
          {
            title: 'External PO Number',
            key: 'external_po_number',
            width: 135,
            dataIndex: 'external_po_number',
            hidden: true,
          },
          {
            title: 'Consolidate Invoice Number',
            key: 'consolidate_po_number',
            width: 135,
            dataIndex: 'consolidate_po_number',
            hidden: true,
          },
          {
            title: 'Purchase Request',
            key: 'purchase_request_number',
            width: 150,
            render: record => {
              return (
                <>
                  <span className="m-label">Purchase Request</span>
                  {record?.purchase_request_number}
                </>
              );
            },
          },
          {
            title: 'Region',
            key: 'region',
            width: 85,
            filters: (user.regions || []).map(item => ({
              text: item.trim(),
              value: item.trim(),
            })),
            render: record => {
              return (
                <>
                  <span className="m-label">Region</span>
                  {record?.region}
                </>
              );
            },
          },
          {
            title: 'Assignee Region Identifier',
            key: 'assignee_region_identifier',
            width: 175,
            render: record => {
              return (
                <>
                  <span className="m-label">Assignee Region Identifier</span>
                  {!record?.assignee ? (
                    '-'
                  ) : (
                    <>
                      <b>{record?.assignee?.full_name || '-'}</b>
                      <br />
                      Region: {record?.assignee_region_identifier || '-'}
                    </>
                  )}
                </>
              );
            },
          },
          {
            title: 'Assignee User',
            width: 135,
            key: 'assignee_user_name',
            dataIndex: 'assignee_user_name',
            hidden: true, // hidden, because only used on download order
          },
          {
            title: 'Requester Name',
            key: 'requester_name',
            filterDropdown: TableFilterAdvanced({
              endpoint: 'entity/buyer/list',
              errorMsg: 'Failed get requester list',
              searchPlaceholder: 'Search requester',
              method: 'GET',
              parser: {
                value: item => item.id,
                label: item => (
                  <span>
                    <b>{item.name}</b> <i>({item.email})</i>
                  </span>
                ),
              },
            }),
            filter_key: 'requester_name',
            width: 155,
            render: (text, record) => {
              if (!record.requester_name) {
                return '';
              }

              return (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span className="m-label">requester Name</span>
                  <span>{record?.requester_name}</span>
                  <span style={{ fontSize: 12 }}>
                    Email: {record?.requester_email}
                  </span>
                </div>
              );
            },
          },
          {
            title: 'Recipient Email',
            dataIndex: 'buyer_email',
            key: 'buyer_email',
            sorter: true,
            width: 155,
            render: text => (
              <>
                <span className="m-label">RecipientEmail</span>
                <div
                  style={{
                    minWidth: '120px',
                    wordWrap: 'break-word',
                    wordBreak: 'break-all',
                    fontSize: '12px',
                  }}
                >
                  {text}
                </div>
              </>
            ),
          },
          {
            title: 'Recipient Name',
            key: 'buyer_name',
            sorter: true,
            filterDropdown: TableFilterAdvanced({
              endpoint: 'entity/buyer/list',
              errorMsg: 'Failed get buyer list',
              searchPlaceholder: 'Search Buyer',
              method: 'GET',
              parser: {
                value: item => String(item.id),
                label: item => (
                  <span>
                    <b>{item.name}</b> <i>({item.email})</i>
                  </span>
                ),
              },
            }),
            filterKey: 'buyer_name',
            width: 150,
            render: record => {
              return (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span className="m-label">Recipient Name</span>
                  <span>{record?.buyer_name}</span>
                  <span>Ph: {record?.buyer_phone_number}</span>
                </div>
              );
            },
          },
          {
            title: 'Entity Name',
            key: 'entity_name',
            sorter: true,
            width: 155,
            render: record => {
              return (
                <>
                  <span className="m-label">Entity Name</span>
                  {record?.entity_name}
                </>
              );
            },
          },
          {
            title: 'Shipping Address',
            key: 'shipping_address',
            dataIndex: 'shipping_address',
            width: 250,
            render: (text, record) => (
              <>
                <span className="m-label">Shipping Address</span>
                <div
                  style={{
                    minWidth: '200px',
                  }}
                >
                  {text}
                </div>
                <ShippingAddressFromER
                  shippingAddressFromER={record.shipping_address_from_er}
                />
              </>
            ),
          },
          {
            title: 'Exchange Rate',
            width: 135,
            key: 'exchange_rate',
            dataIndex: 'exchange_rate',
            hidden: false,
          },
          {
            title: 'Total',
            key: 'total_price_all',
            sorter: true,
            width: 120,
            render: record => {
              let totalOrder = record.total_price_all || 0;

              if (record.region === 'JP') {
                totalOrder = Math.round(totalOrder);
              }

              return (
                <>
                  <span className="m-label">Total Price</span>
                  {totalOrder}
                </>
              );
            },
          },
          {
            title: 'Total Cost',
            key: 'total_cost',
            width: 120,
            render: record => {
              return (
                <>
                  <span className="m-label">Total Cost</span>
                  {record?.supplier_payment_list
                    ? record?.supplier_payment_list.reduce(function(acc, val) {
                        return acc + val.total_payable;
                      }, 0)
                    : 0}
                </>
              );
            },
          },
          // {
          //   title: 'Amount Paid To Supplier',
          //   width: 120,
          //   render: record => {
          //     return (
          //       <>
          //         <span className="m-label">Amount Paid To Supplier</span>
          //         {record?.amount_paid_to_supplier}
          //       </>
          //     );
          //   },
          // },
          // {
          //   title: 'Margin [Total - %]',
          //   width: 135,
          //   render: record => {
          //     return (
          //       <>
          //         <span className="m-label">Margin [Total - %]</span>
          //         {`${record?.margin} - ${record?.margin_percentage}%`}
          //       </>
          //     );
          //   },
          // },
          {
            title: 'Current Tax',
            width: 135,
            dataIndex: 'current_tax',
            key: 'current_tax',
          },
          {
            title: 'All Qty',
            width: 135,
            dataIndex: 'all_qty',
            key: 'all_qty',
            hidden: false, // hidden, because only used on download order
          },
          {
            title: 'All Fulfilled Qty',
            width: 135,
            dataIndex: 'all_fulfilled_qty',
            key: 'all_fulfilled_qty',
            hidden: false,
          },
          {
            title: 'Total Fulfilled Amount',
            width: 135,
            dataIndex: 'all_fulfilled_amt',
            key: 'all_fulfilled_amt',
            hidden: false,
          },
          {
            title: 'Handling Fee',
            width: 135,
            dataIndex: 'handling_fee',
            key: 'handling_fee',
            hidden: true,
          },
          {
            title: 'Total Margin',
            width: 135,
            dataIndex: 'cumulative_margin',
            key: 'cumulative_margin',
            filterDropdown: MarginRangeFilter,
          },
          {
            title: 'Cumulative Margin (%)',
            key: 'cumulative_margin_percentage',
            width: 160,
            render: record => {
              let percentMargin = record.cumulative_margin_percentage || 0;

              return (
                <>
                  <span className="m-label">Cumulative Margin (%)</span>
                  {percentMargin}
                </>
              );
            },
          },
          {
            title: 'Currency',
            key: 'currency',
            width: 100,
            render: record => {
              return (
                <>
                  <span className="m-label">Currency</span>
                  {`${record?.currency ||
                    getCurrencyFromRegion(record?.region)}`}
                </>
              );
            },
          },
          {
            title: 'Operation Status',
            key: 'operation_status',
            sorter: true,
            width: 225,
            filters: [
              OrderOperationStatus.ORDER_CREATED,
              OrderOperationStatus.PENDING_APPROVAL,
              OrderOperationStatus.APPROVED,
              OrderOperationStatus.PENDING_DELIVERY,
              OrderOperationStatus.PENDING_DO_UPLOAD,
              OrderOperationStatus.COMPLETED,
              OrderOperationStatus.PO_CANCELLED,
              OrderOperationStatus.REJECTED,
              OrderOperationStatus.COMPLETED_TENTATIVE,
            ].map(item => ({
              text: item,
              value: item,
            })),
            render: record => {
              const operationStatus = record.operation_status
                ?.split('_')
                ?.join(' ');
              return (
                <>
                  <span className="m-label">Operation Status</span>
                  <Tag color={ColorMap[operationStatus]}>{operationStatus}</Tag>
                  <OrderShipmentStatus
                    record={record}
                    onSuccess={() => refreshList()}
                  />
                </>
              );
            },
          },
          {
            title: 'Payment Status',
            key: 'payment_status',
            sorter: true,
            width: 230,
            filters: paymentStatus.map(item => ({ text: item, value: item })),
            render: record => (
              <>
                <OrderPaymentStatus record={record} />
                <OrderEmailOrderPicker
                  record={record}
                  refreshList={async () => {
                    refreshList();
                  }}
                />
              </>
            ),
          },
          {
            title: 'Issue Date',
            key: 'issue_date',
            width: 120,
            sorter: true,
            render: record => {
              return (
                <>
                  <span className="m-label">Issue Date</span>
                  {formatUTCTimeToLocal(record?.issue_date, 'DD MMM YYYY')}
                </>
              );
            },
          },
          {
            title: 'Invoice Date',
            key: 'invoice_date',
            width: 150,
            sorter: true,
            render: record => {
              return (
                <>
                  <span className="m-label">Invoice Date</span>
                  {formatUTCTimeToLocal(
                    record?.invoice_date,
                    'DD MMM YYYY'
                  )}{' '}
                  <EditInvoiceDateInTable
                    date={record?.invoice_date}
                    orderId={record.id}
                    refreshList={() => toggleLoad()}
                  />{' '}
                  <OrderInvoiceDateHistory
                    orderId={record.id}
                    date={record?.invoice_date}
                  />
                </>
              );
            },
          },
          {
            title: 'DO Date',
            key: 'do_date',
            width: 150,
            sorter: true,
            render: record => {
              return (
                <>
                  <span className="m-label">DO Date</span>
                  {formatUTCTimeToLocal(record?.do_date, 'DD MMM YYYY')}{' '}
                  <EditDoDateInTable
                    date={record?.do_date}
                    orderId={record.id}
                    refreshList={() => toggleLoad()}
                  />{' '}
                  <OrderDoDateHistory
                    orderId={record.id}
                    date={record?.do_date}
                  />
                </>
              );
            },
          },
          {
            title: 'Payment Date',
            key: 'payment_date',
            sorter: true,
            width: 150,
            render: record => {
              return (
                <OrderPaymentDate
                  value={record.payment_date}
                  orderId={record.id}
                  status={record.payment_status}
                />
              );
            },
          },
          {
            title: 'Reason for Request to Cancel SAID',
            filters: reasonOptions.map(item => ({
              text: item,
              value: item,
            })),
            key: 'reason',
            dataIndex: 'reason',
            render: (value, record) => {
              return (
                <>
                  <p style={{ fontSize: '12px' }}>{value ? value : ''}</p>

                  <ul style={{ padding: '0px', listStyleType: 'none' }}>
                    {record?.supplier_payment
                      .filter(item => item?.created_by_id !== 80)
                      .map(item => (
                        <li style={{ fontSize: '12px' }}>
                          SPID: {item?.id}{' '}
                          {item?.reject_reason
                            ? `(${item?.reject_reason})`
                            : '--'}
                        </li>
                      ))}
                  </ul>
                </>
              );
            },
            width: 120,
          },
          {
            title: 'Days to Fulfillment',
            key: 'days_to_fulfill',
            width: 120,
            render: record => {
              return (
                <>
                  <span className="m-label">Days to Fulfillment</span>
                  {record?.days_to_fulfill}
                </>
              );
            },
          },
          {
            title: 'Xero Sync Status',
            width: 135,
            key: 'xero_sync_status',
            dataIndex: 'xero_sync_status',
            hidden: true, // hidden, because only used on download order
          },
          {
            title: 'Xero Sync',
            key: 'xero_sync',
            width: 170,
            render: record => {
              const syncedAt = record.xero_sync_date;
              const syncedBy = record.xero_sync_user;
              const syncHistory = record.xero_sync_payload || {};
              const historyKeys = Object.keys(syncHistory);

              return (
                <>
                  <XeroSyncButton
                    id={record.id}
                    xero_invoice_id={record.xero_invoice_id}
                    endpoint={URL_ORDER_SYNC_TO_XERO}
                  />
                  {!!syncedAt && (
                    <>
                      <p style={{ margin: 0 }}>Synced at:</p>
                      <p style={{ margin: 0 }}>
                        {formatUTCTimeToLocal(syncedAt, 'HH:mm, DD MMM YYYY')}
                      </p>
                    </>
                  )}
                  {!!syncedBy && (
                    <>
                      <p style={{ margin: 0 }}>
                        by: {syncedBy.full_name} <i>({syncedBy.username})</i>
                      </p>
                    </>
                  )}
                  {historyKeys.length > 0 && (
                    <XeroSyncHistory
                      history={syncHistory}
                      placement="rightBottom"
                    />
                  )}
                </>
              );
            },
          },
          {
            title: 'Is Consolidated Line Items',
            width: 120,
            key: 'is_consolidated_line_items',
            filters: [
              {
                text: 'Yes',
                value: 'yes',
              },
              {
                text: 'No',
                value: 'no',
              },
            ],
            filterMultiple: false,
            render: record => {
              return (
                <>
                  <span className="m-label">Is Consolidated Line Items</span>
                  {record?.is_consolidated_line_items ? 'Yes' : 'No'}
                </>
              );
            },
          },
          {
            title: 'NCPR',
            key: 'is_ncpr',
            sorter: true,
            width: 120,
            render: record => {
              return <>{record.is_ncpr ? <span>Yes</span> : <span>No</span>}</>;
            },
          },
          {
            title: 'Created At',
            key: 'created_at',
            sorter: true,
            width: 120,
            render: record => {
              return (
                <>
                  <span className="m-label">Created At</span>
                  {formatUTCTimeToLocal(record.created_at, 'DD-MM-YYYY')}
                </>
              );
            },
          },
          {
            title: 'Created By',
            key: 'created_by',
            dataIndex: ['user', 'full_name'],
            width: 100,
            filters: (users.data?.list || []).map(user => ({
              text: user.full_name,
              value: user.id,
            })),
            filterSearch: true,
          },
        ],
        sortedColumns
      ),
    ],
    responseParser: record => {
      return {
        ...record,
        items: record.items.map(item => ({
          ...item,
          tax_rate: !!record.is_taxable
            ? record.is_region_tax
              ? (record.region_tax_rate || 0) * 100
              : item.tax_rate || 0
            : null,
        })),
      };
    },
  });

  const table = useTableState[0];
  const toggleLoad = useTableState[1];
  const data = useTableState[2];
  const buildPayload = useTableState[4];

  // Collect all SPIDs across all order items for bulk status fetch
  const allSpids = useMemo(() => {
    const items: Array<{ items?: Array<{ spid_no?: number[] }> }> =
      data?.items || [];
    const spids: number[] = [];
    const seen = new Set<number>();
    items.forEach(record =>
      (record.items || []).forEach(item =>
        (item.spid_no || []).forEach(spid => {
          if (!seen.has(spid)) {
            seen.add(spid);
            spids.push(spid);
          }
        })
      )
    );
    return spids;
  }, [data]);

  const spidStatuses = useQuery<
    Array<{
      id: number;
      status: string;
      problem_line_items?: Array<{ order_item_id: number; status: string }>;
    }>
  >(
    ['spid-statuses-v1', allSpids.sort()],
    async () => {
      if (!allSpids.length) return [];
      try {
        const resp = await fetchAPI('supplier-payments/statuses', {
          method: 'POST',
          payload: { spids: allSpids },
        });
        return resp?.ok || [];
      } catch (error) {
        return [];
      }
    },
    {
      staleTime: 5 * 60 * 1000,
      enabled: allSpids.length > 0,
      placeholderData: [],
    }
  );

  const spidStatusMap = useMemo(() => {
    const map: Record<number, string> = {};
    (spidStatuses.data || []).forEach(item => {
      map[item.id] = item.status;
    });
    return map;
  }, [spidStatuses.data]);

  const spidProblemStatusMap = useMemo(() => {
    const map: Record<number, Record<number, string>> = {};
    (spidStatuses.data || []).forEach(item => {
      const inner: Record<number, string> = {};
      (item.problem_line_items || []).forEach(pl => {
        inner[pl.order_item_id] = pl.status;
      });
      map[item.id] = inner;
    });
    return map;
  }, [spidStatuses.data]);
  const payload = buildPayload();
  const [form] = Form.useForm();

  useEffect(() => {
    const savedColumnsRaw = localStorage.getItem(orderV1ColumnsKey);
    if (savedColumnsRaw) {
      const savedColumns = safelyParseJSON(savedColumnsRaw);
      setSortedColumns(savedColumns);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <StyledContainer>
      <Drawer
        visible={openModalDownloadBulkInvoice}
        title="Download Bulk Invoice"
        width={'500px'}
        onClose={() => setOpenModalDownloadBulkInvoice(false)}
      >
        <DownloadBulkInvoice
          closeModal={() => setOpenModalDownloadBulkInvoice(false)}
        />
      </Drawer>
      {visible && (
        <Drawer
          visible
          headerStyle={{ position: 'relative' }}
          title={
            <div>
              <span>
                {!!orderId ? `Update Order ${orderId}` : `Create Order`}
              </span>
              <span
                style={{ position: 'absolute', right: '20px', top: '10px' }}
              >
                <Button onClick={form.submit} type="primary">
                  Save Order
                </Button>
              </span>
            </div>
          }
          placement={isSmallScreen ? 'left' : 'right'}
          width={isSmallScreen ? '90%' : '900px'}
          closable={false}
          onClose={() => {
            form.resetFields();
            toggle();
          }}
        >
          <OrderCreate
            users={users.data?.list || []}
            form={form}
            onOk={(product: Product) => {
              toggle();
              refreshList();
            }}
            refreshDetailData={async () => {
              await handleClickEditOrder(orderId, true);
            }}
          />
        </Drawer>
      )}

      <Row gutter={32} style={{ marginBottom: '10px' }}>
        <Col lg={5} sm={24}>
          <Statistic
            title="Pending Amount to Suppliers"
            value={formatPrice(
              get(data, 'stats.pending_amount_to_suppliers', 0)
            )}
          />
        </Col>
        {/* <Col lg={4} sm={24}>
          <Statistic
            title="Amount Paid to Suppliers"
            value={formatPrice(get(data, 'stats.amount_paid_to_supplier', 0))}
          />
        </Col>
        <Col lg={5} sm={24}>
          <Statistic
            title="Pending Payment from Buyer to SS"
            value={formatPrice(get(data, 'stats.pending_payment', 0))}
          />
        </Col>
        <Col lg={4} sm={24}>
          <Statistic
            title="Paid by Buyer to SS"
            value={formatPrice(get(data, 'stats.paid', 0))}
          />
        </Col> */}
        <TotalPOProvider payload={payload}>
          <Col lg={5} sm={24}>
            <OrderTotalPOValues />
          </Col>
          <Col lg={5} sm={24}>
            <OrderTotalPOFulfilledValues />
          </Col>
          <Col lg={5} sm={24}>
            <OrderTotalClientPaidValues />
          </Col>
          <Col lg={4} sm={24}>
            <OrderCorrectedEmailReader />
          </Col>
          <Col lg={4} sm={24}>
            <OrderFXRates />
          </Col>
        </TotalPOProvider>
      </Row>
      {table}

      <Drawer
        title="Invoice Consolidation"
        visible={invoiceConsolidationDrawer}
        width="920"
        onClose={() => setInvoiceConsolidationDrawer(false)}
      >
        <InvoiceConsolidation />
      </Drawer>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  /* @media (min-width: 991px) {
    min-width: 1500px;
  } */
  .ant-table-tbody > tr .ant-table {
    margin: 10px !important;
  }
  .ant-table-row.ant-table-row-level-0.editable-row {
    background-color: #fff;
  }
  .ant-table-tbody > tr.ant-table-row:hover > td {
    background: #e0e0f8;
  }
`;

const StyledDiv = styled.div`
  min-width: 90px;
  button,
  a {
    margin: 5px;
  }

  p.action-label {
    font-weight: 600;
    margin: 0px 0px 5px 10px;

    &.finance {
      margin-top: 10px;
    }
  }
`;

export const StatusTimeline = styled.div`
  min-height: 50px;
  min-width: 50px;

  .ant-timeline {
    margin-top: 15px;
  }

  .ant-timeline-item {
    font-size: 12px;
    &:last-child {
      padding-bottom: 0px;
    }
  }
`;

export const ExpanderBtnContainer = styled.div`
  display: flex;
  flex-direction: row-reverse;
  padding: 0 10px;
`;

const UlWrapper = styled.ul`
  li:hover {
    color: blue;
  }
`;

const MarginRangeFilter = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
  visible,
  close,
}) => {
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);

  useEffect(() => {
    if (visible) {
      const safeSelectedKeys = selectedKeys || ['0', '0'];
      const sStart = safeSelectedKeys[0];
      const eEnd = safeSelectedKeys[1];
      setStart(Number(sStart) || null);
      setEnd(Number(eEnd) || null);
    }
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MarginRangeContainer>
      <Row gutter={[10, 10]}>
        <Col span={12}>
          <p>Margin From</p>
          <InputNumber
            value={start}
            onChange={val => setStart(val)}
            placeholder="Start"
          />
        </Col>
        <Col span={12}>
          <p>To</p>
          <InputNumber
            value={end}
            onChange={val => setEnd(val)}
            placeholder="End"
          />
        </Col>
      </Row>
      <div className="btn-container">
        <Button
          type="link"
          onClick={() => {
            clearFilters();
            setStart(null);
            setEnd(null);
          }}
        >
          Reset
        </Button>
        <Button
          type="primary"
          onClick={() => {
            setSelectedKeys([start, end]);
            confirm();
          }}
        >
          Apply
        </Button>
      </div>
    </MarginRangeContainer>
  );
};

const implementSortedOrderColumns = (
  originalColumns: any[],
  sortedColumns: OrderColumn[]
) => {
  const activeColumns = sortedColumns.reduce((arr, colObj) => {
    if (!colObj.active) {
      return arr;
    }

    const tableColObj = originalColumns.find(obj => obj.key === colObj.key);
    if (!tableColObj) {
      return arr;
    }

    return [...arr, tableColObj];
  }, []);

  return activeColumns;
};

const MarginRangeContainer = styled.div`
  padding: 10px;

  .btn-container {
    display: flex;
    margin: 10px 0 0;
    justify-content: space-between;
  }
`;
